/**
 * PRTL-1495: the one recurrence shape a Compass Call series can have, stored
 * as an RFC 5545 RRULE on `Meeting.rrule`:
 *
 *     FREQ=MONTHLY[;INTERVAL=n];BYDAY=<1|2|3|4|-1><SU|MO|TU|WE|TH|FR|SA>
 *
 * "The 4th Thursday of every month", "the last Friday every 3 months" (n is
 * 2–12; INTERVAL is left out when it's 1). DTSTART, TZID, UNTIL and COUNT are
 * never in the string: the series' `startDate`, `timeZone` and
 * `generatedThrough` columns carry them. Anything else is rejected, so the
 * admin form can always edit what's stored.
 *
 * Pure and dependency-free (zod only), so the web and navigator can label a
 * series without ical.js; expansion lives in @compass/services
 * (`meetingRecurrence`).
 */
import { z } from "zod";

export const MEETING_RULE_ORDINALS = [1, 2, 3, 4, -1] as const;
export type MeetingRuleOrdinal = (typeof MEETING_RULE_ORDINALS)[number];

/** RFC 5545 day codes, indexed by 0 = Sunday … 6 = Saturday. */
const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

export interface MeetingRule {
    /** 1–4 for the 1st–4th, -1 for the last. */
    ordinal: MeetingRuleOrdinal;
    /** 0 = Sunday … 6 = Saturday. */
    weekday: number;
    /** Months between occurrences, 1–12 (3 = quarterly, 12 = yearly). */
    intervalMonths: number;
}

const RULE = /^FREQ=MONTHLY(?:;INTERVAL=([2-9]|1[0-2]))?;BYDAY=(-1|[1-4])(SU|MO|TU|WE|TH|FR|SA)$/;

/** The rule's parts, or null when it isn't exactly the accepted shape. */
export function parseMeetingRule(rrule: string): MeetingRule | null {
    const m = RULE.exec(rrule);
    if (!m) return null;
    const weekday = DAY_CODES.indexOf(m[3] as (typeof DAY_CODES)[number]);
    return {
        ordinal: Number(m[2]) as MeetingRuleOrdinal,
        weekday,
        intervalMonths: m[1] ? Number(m[1]) : 1,
    };
}

export const meetingRuleSchema = z.string().refine((rule) => parseMeetingRule(rule) !== null, {
    message:
        "A recurring Compass Call repeats on the 1st, 2nd, 3rd, 4th or last weekday of every 1–12 months (FREQ=MONTHLY[;INTERVAL=n];BYDAY=<ord><day>)",
});

/** The parts a rule is built from; throws on values outside the accepted shape. */
export const meetingRulePartsSchema = z.object({
    ordinal: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]),
    weekday: z.number().int().min(0).max(6),
    intervalMonths: z.number().int().min(1).max(12),
});

/** "FREQ=MONTHLY;INTERVAL=3;BYDAY=2TU". Throws if a part is out of range. */
export function buildMeetingRule(parts: MeetingRule): string {
    const { ordinal, weekday, intervalMonths } = meetingRulePartsSchema.parse(parts);
    const interval = intervalMonths === 1 ? "" : `;INTERVAL=${intervalMonths}`;
    return `FREQ=MONTHLY${interval};BYDAY=${ordinal}${DAY_CODES[weekday]}`;
}

/**
 * The week of the month a day falls in, as an ordinal: the 1st–4th, or -1
 * when it's the 5th (29th or later). The 22nd–28th count as the 4th, which
 * every month has, rather than the last, which moves. What a picker defaults
 * to for a start date.
 */
export function ordinalForDayOfMonth(day: number): MeetingRuleOrdinal {
    const week = Math.ceil(day / 7);
    return week >= 5 ? -1 : (week as 1 | 2 | 3 | 4);
}
